import React, { useEffect, useMemo, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { authApi } from '../api/auth';
import { supportApi } from '../api/support';
import { useLanguage } from '../i18n/LanguageContext';
import '../styles/support-widget.css';

const TELEGRAM_SUPPORT_URL = 'https://t.me/dios10_supportBot';

const SupportWidget = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasEscalated, setHasEscalated] = useState(false);
  const [lastBotReply, setLastBotReply] = useState('');
  const [lastUserMessage, setLastUserMessage] = useState('');
  const user = useMemo(() => authApi.getCurrentUser(), []);
  const quickLinks = useMemo(() => ([
    { label: t('supportWidget.quickHome'), href: '/' },
    { label: t('supportWidget.quickMarketplace'), href: '/marketplace' },
    { label: t('supportWidget.quickTournaments'), href: '/tournaments' },
    { label: t('supportWidget.quickRankings'), href: '/rankings' },
    { label: t('supportWidget.quickDeposit'), href: '/deposit' },
    { label: t('supportWidget.quickProfile'), href: '/profile' }
  ]), [t]);

  const openWidget = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      setMessages([
        {
          sender: 'bot',
          kind: 'welcome',
          text: t('supportWidget.welcomeMessage'),
          telegramUrl: TELEGRAM_SUPPORT_URL
        }
      ]);
    }
  };
  useEffect(() => {
    setMessages((prev) => prev.map((message) => (
      message.kind === 'welcome'
        ? { ...message, text: t('supportWidget.welcomeMessage') }
        : message
    )));
  }, [t]);


  const closeWidget = () => setIsOpen(false);

  const handleSend = async () => {
    if (!input.trim()) return;
    const text = input.trim();
    setInput('');
    setHasEscalated(false);
    setLastUserMessage(text);
    setMessages((prev) => [...prev, { sender: 'user', text }]);
    setLoading(true);
    try {
      const response = await supportApi.ask(text);
      if (response?.success) {
        setMessages((prev) => [...prev, { sender: 'bot', text: response.reply }]);
        setLastBotReply(response.reply);
      } else {
        setMessages((prev) => [...prev, { sender: 'bot', text: t('supportWidget.fallbackNotUnderstood') }]);
        setLastBotReply(t('supportWidget.fallbackNotUnderstood'));
      }
    } catch (error) {
      setMessages((prev) => [...prev, { sender: 'bot', text: t('supportWidget.temporaryError') }]);
      setLastBotReply(t('supportWidget.temporaryError'));
    } finally {
      setLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!user?.id) {
      setMessages((prev) => [...prev, { sender: 'bot', text: t('supportWidget.loginRequired') }]);
      return;
    }
    const message = (input.trim() || lastUserMessage || '').trim();
    if (!message) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: t('supportWidget.writeRequestHint')
        }
      ]);
      return;
    }
    setLoading(true);
    try {
      const response = await supportApi.escalate({
        userId: user.id,
        message,
        botReply: lastBotReply || undefined
      });
      if (response?.success) {
        setHasEscalated(true);
        setInput('');
        setMessages((prev) => [
          ...prev,
          {
            sender: 'bot',
            text: t('supportWidget.escalatedTicket', { ticketId: response.ticket.ticketId })
          }
        ]);
      }
    } catch (error) {
      setMessages((prev) => [...prev, { sender: 'bot', text: t('supportWidget.escalateFailed') }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-widget">
      {!isOpen && (
        <button className="support-widget-button" onClick={openWidget} aria-label={t('supportWidget.title')}>
          <MessageCircle className="w-5 h-5" />
          <span>{t('supportWidget.buttonOpen')}</span>
        </button>
      )}
      {isOpen && (
        <div className="support-widget-panel">
          <div className="support-widget-header">
            <div className="support-widget-title">{t('supportWidget.title')}</div>
            <button type="button" className="support-widget-close modal-close-btn" onClick={closeWidget} aria-label={t('supportWidget.closeAria')}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="support-widget-messages">
            {messages.map((message, index) => (
              <div key={`${message.sender}-${index}`} className={`support-message ${message.sender}`}>
                {message.text}
                {message.sender === 'bot' && message.telegramUrl && (
                  <a
                    className="support-telegram-btn"
                    href={message.telegramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Send className="w-4 h-4" />
                    <span>{t('supportWidget.openTelegram')}</span>
                  </a>
                )}
              </div>
            ))}
            {loading && <div className="support-message bot">{t('supportWidget.loadingReply')}</div>}
          </div>
          <div className="support-widget-actions">
            <div className="support-widget-links">
              {quickLinks.map((link) => (
                <a key={link.href} className="support-widget-link" href={link.href}>
                  {link.label}
                </a>
              ))}
            </div>
            {!hasEscalated && (
              <button className="support-widget-escalate" onClick={handleEscalate} disabled={loading}>
                {t('supportWidget.escalateAction')}
              </button>
            )}
          </div>
          <div className="support-widget-input">
            <input
              type="text"
              placeholder={t('supportWidget.inputPlaceholder')}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSend();
                }
              }}
            />
            <button onClick={handleSend} disabled={loading || !input.trim()} aria-label={t('supportWidget.sendAria')}>
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportWidget;
